import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MessagingChannelType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import ToggleSidebarButton from '~components/Buttons/ToggleSidebarButton';
import UnreadCountBadge from '~components/Messaging/UnreadCountBadge';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import DebouncedSearchBox from '~components/Search/DebouncedSearchBox';
import Tabs, { TabProps } from '~components/Tabs/Tabs';
import useMessagingConversationNavigation from '~hooks/useMessaging/useMessagingConversationNavigation';
import { APPBAR_HEIGHT_IN_PX } from '~pages/Dispatch/components/drivers/constants';
import MessagingChannelList from '~pages/Messages/components/MessagingChannelList';
import NewConversationDialog from '~pages/Messages/components/NewConversationDialog';
import { messagingChannelTypeFilterLabel } from '~pages/Messages/messagingChannelLabels';
import MessagingConversation from '~pages/Messages/MessagingConversation';
import MessagingProviders from '~pages/Messages/MessagingProviders';
import useMessagingListView, {
  MESSAGING_LIST_VIEWS,
  MessagingListView,
} from '~pages/Messages/useMessagingListView';
import useMessagingChannelsInfiniteQuery from '~queries/useMessagingChannelsInfiniteQuery';
import useMessagingUnreadCountsQuery from '~queries/useMessagingUnreadCountsQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const ALL_CHANNEL_TYPES = 'all';

// Sized like the Live Map sidebar so both list-beside-detail screens match.
const CHANNEL_RAIL_WIDTH_IN_PX = 500;
const NARROW_CHANNEL_RAIL_WIDTH_IN_PX = 450;
const COLLAPSED_CHANNEL_RAIL_WIDTH_IN_PX = 50;

const listViewLabelKey = {
  archived: 'messaging.list_views.archived',
  inbox: 'messaging.list_views.inbox',
  unread: 'messaging.list_views.unread',
} satisfies Record<MessagingListView, string>;

type ChannelTypeFilter = MessagingChannelType | typeof ALL_CHANNEL_TYPES;

const FILTERED_CHANNEL_TYPES = [
  MessagingChannelType.BROADCAST,
  MessagingChannelType.ORDER,
  MessagingChannelType.JOB,
];

/**
 * The channel id is in the URL for deep links, but selects a pane rather than a page.
 * The negative margins cancel the shell's horizontal padding.
 */
const Messages = observer(() => {
  const theme = useTheme();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  // The api refuses every channel type without create_messaging_channel, broadcast
  // included, so a broadcast-only user can start nothing from here.
  const { canCreateMessagingChannel } = userStore.getPermissions();
  const { openConversation, openConversationList } = useMessagingConversationNavigation();
  const { channelId } = useParams<{ channelId: string }>();
  const isSinglePane = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedChannelType, setSelectedChannelType] =
    useState<ChannelTypeFilter>(ALL_CHANNEL_TYPES);
  const [searchQuery, setSearchQuery] = useState('');
  const { listView, setListView } = useMessagingListView();
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);
  const [isRailCollapseRequested, setIsRailCollapseRequested] = useState(false);

  // Collapsing on a single pane would leave nothing on screen to expand it from.
  const isRailCollapsed = isRailCollapseRequested && !isSinglePane;

  const {
    channels,
    fetchMore,
    hasLoadFailed,
    hasMore,
    isLoading,
    isLoadingMore,
    retryLoad,
  } = useMessagingChannelsInfiniteQuery({
    archived: listView === 'archived',
    channelType:
      selectedChannelType === ALL_CHANNEL_TYPES ? undefined : selectedChannelType,
    hasUnread: listView === 'unread' ? true : undefined,
    searchQuery,
  });

  const { unreadCounts } = useMessagingUnreadCountsQuery();

  const unreadBadgeFor = (count: number) =>
    count > 0 ? <UnreadCountBadge count={count} /> : undefined;

  const listViewTabs: TabProps<MessagingListView>[] = MESSAGING_LIST_VIEWS.map(
    (view) => ({
      endAdornment: view === 'unread' ? unreadBadgeFor(unreadCounts.total) : undefined,
      label: t(listViewLabelKey[view]),
      value: view,
    }),
  );

  const channelTypeFilters: TabProps<ChannelTypeFilter>[] = [
    {
      endAdornment: unreadBadgeFor(unreadCounts.total),
      label: t('messaging.all_channel_types'),
      value: ALL_CHANNEL_TYPES,
    },
    ...FILTERED_CHANNEL_TYPES.map((channelType) => ({
      endAdornment: unreadBadgeFor(unreadCounts.forChannelType(channelType)),
      label: messagingChannelTypeFilterLabel(channelType),
      value: channelType,
    })),
  ];

  const isRailVisible = !isSinglePane || !channelId;
  const isConversationVisible = !isSinglePane || Boolean(channelId);

  const channelRailWidth = (expandedWidthInPx: number) => {
    if (isSinglePane) return '100%';

    return isRailCollapsed
      ? `${COLLAPSED_CHANNEL_RAIL_WIDTH_IN_PX}px`
      : `${expandedWidthInPx}px`;
  };

  return (
    <MessagingProviders>
      <Box
        sx={{
          display: 'flex',
          height: `calc(100vh - ${APPBAR_HEIGHT_IN_PX}px - var(--tread-agent-dock-height, 0px))`,
          marginLeft: -5,
          marginRight: -5,
          overflow: 'hidden',
        }}
      >
        {isRailVisible && (
          <Box
            sx={{
              backgroundColor: theme.semantic.surface,
              borderRight: isSinglePane
                ? undefined
                : `1px solid ${theme.semantic.divider}`,
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              minHeight: 0,
              overflow: 'hidden',
              transition: 'width 0.3s',
              width: channelRailWidth(CHANNEL_RAIL_WIDTH_IN_PX),
              [theme.breakpoints.down('lg')]: {
                width: channelRailWidth(NARROW_CHANNEL_RAIL_WIDTH_IN_PX),
              },
            }}
          >
            <Box sx={{ flexShrink: 0, px: 2, py: 1 }}>
              {!isRailCollapsed && (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="h5">{t('page_headings.messages')}</Typography>

                  {canCreateMessagingChannel && (
                    <SmallButton
                      color="brandV2Yellow"
                      disabled={!companyId}
                      onClick={() => setIsNewConversationOpen(true)}
                    >
                      {t('messaging.new_message')}
                    </SmallButton>
                  )}
                </Box>
              )}

              {!isRailCollapsed && (
                <Tabs
                  ariaLabel={t('messaging.list_views.label')}
                  onChange={setListView}
                  selected={listView}
                  sx={{ mb: 1 }}
                  tabs={listViewTabs}
                  variant="underline"
                />
              )}

              <Box sx={{ alignItems: 'flex-start', display: 'flex' }}>
                {!isRailCollapsed && (
                  <Tabs
                    onChange={setSelectedChannelType}
                    selected={selectedChannelType}
                    sx={{ mb: 1, width: '100%' }}
                    // Four tabs do not fit at the default minimum; the bar cannot scroll.
                    tabSx={{ minWidth: 0 }}
                    tabs={channelTypeFilters}
                  />
                )}

                {!isSinglePane && (
                  <ToggleSidebarButton
                    isCollapsed={isRailCollapsed}
                    onClick={() => setIsRailCollapseRequested(!isRailCollapseRequested)}
                    sx={{ ml: isRailCollapsed ? -0.5 : 1, mt: '3px' }}
                  />
                )}
              </Box>

              {!isRailCollapsed && (
                <DebouncedSearchBox
                  // Collapsing the rail unmounts the box while the search stays in force, so
                  // reopening it has to show the query that is still filtering the list.
                  initialValue={searchQuery}
                  onChange={setSearchQuery}
                  placeholder={t('messaging.search_placeholder')}
                />
              )}
            </Box>

            {!isRailCollapsed && (
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <MessagingChannelList
                  channels={channels}
                  hasLoadFailed={hasLoadFailed}
                  hasMore={hasMore}
                  isFilteringUnread={listView === 'unread'}
                  isLoading={isLoading}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={fetchMore}
                  onRetryLoad={retryLoad}
                  onSelectChannel={(channel) => openConversation(channel.id)}
                  searchQuery={searchQuery}
                  selectedChannelId={channelId}
                  viewerUserId={userStore.user?.id}
                />
              </Box>
            )}
          </Box>
        )}

        {isConversationVisible && (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            {channelId ? (
              <MessagingConversation
                channelId={channelId}
                onBack={isSinglePane ? openConversationList : undefined}
              />
            ) : (
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: theme.semantic.surfaceSunken,
                  display: 'flex',
                  flex: 1,
                  justifyContent: 'center',
                  p: 4,
                }}
              >
                <Typography sx={{ color: theme.semantic.textSecondary }}>
                  {t('messaging.select_conversation')}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {companyId && isNewConversationOpen && (
          <NewConversationDialog
            isOpen
            onClose={() => setIsNewConversationOpen(false)}
            onCreated={(channel) => {
              setIsNewConversationOpen(false);
              openConversation(channel.id);
            }}
          />
        )}
      </Box>
    </MessagingProviders>
  );
});

export default Messages;
